import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { APP_AUTH_STATE, LogoutOptions } from "blu-utilities";
import { Observable, of, throwError } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { AccountService } from "src/app/modules/authentication/services/account.service";
import { AuthenticationService } from "src/app/modules/authentication/services/authentication.service";
import { AppService } from "src/services/app.service";
import { COOKIES } from "../models/cookie";
import { PAGE_URLS } from "../models/general";
import { BluStorageService } from "./blu-storage.service";
import { HttpService } from "./http.service";
import { PopupNotificationService } from "./popup-notification.service";

@Injectable({
  providedIn: "root",
})
export class LogoutService {
  private isLogoutInProgress = false; // Mutex to handle simultaneous logout attempts

  constructor(
    private popupNotificationsService: PopupNotificationService,
    private authenticationService: AuthenticationService,
    private storageService: BluStorageService,
    private accountService: AccountService,
    private httpService: HttpService,
    private appService: AppService,
    private router: Router,
  ) {}

  /**
   * Logs the user out, handles session removal, and redirects.
   *
   * @param {LogoutOptions} [options] - Optional parameters for the logout.
   * @returns {Observable<any>} - An observable indicating the completion of the logout process.
   */
  logout(options?: LogoutOptions): Observable<any> {
    if (this.isLogoutInProgress) {
      return of([]);
    }

    this.isLogoutInProgress = true;

    if (options?.redirectToReturnUrl) {
      this.storageService.setLocalStorage(COOKIES.REDIRECT_TO_RETURN_URL, true);
    }

    if (!this.appService.getIsLoggingOut()) {
      this.appService.setIsApplicationLoading(true);
      return this.handleLogout(options).pipe(
        finalize(() => {
          this.isLogoutInProgress = false;
        }),
      );
    } else {
      this.removeSessionAndRedirectToLogin(options);
      this.isLogoutInProgress = false;
      return of([]);
    }
  }

  /**
   * Handles the logout process by deleting authentication data.
   *
   * @param {LogoutOptions} [options] - Optional parameters for the logout.
   * @returns {Observable<any>} - An observable that performs the logout process.
   */
  private handleLogout(options?: LogoutOptions): Observable<any> {
    this.appService.setIsLoggingOut(true);
    return this.deleteAuthentication().pipe(
      catchError((error) => {
        return throwError(() => error);
      }),
      finalize(() => {
        this.removeSessionAndRedirectToLogin(options);
      }),
    );
  }

  /**
   * Deletes the authentication data from the server.
   *
   * @returns {Observable<any>} - An observable that deletes the authentication data.
   */
  private deleteAuthentication(): Observable<any> {
    return this.httpService.delete("auth/logout");
  }

  /**
   * Removes the session data and redirects the user to the login page.
   *
   * @param {LogoutOptions} [options] - Optional parameters for the logout.
   */
  removeSessionAndRedirectToLogin(options?: LogoutOptions) {
    this.removeSession();
    this.redirectToLogin(options);
  }

  /**
   * Redirects the user to the login page, with optional return URL.
   *
   * @param {LogoutOptions} [options] - Optional parameters for the logout.
   */
  private redirectToLogin(options?: LogoutOptions) {
    const returnUrl = this.appService.getReturnUrl();
    const retainReturnUrl = !!this.storageService.getFromLocalStorage(
      COOKIES.REDIRECT_TO_RETURN_URL,
    );
    const queryParams =
      !options?.skipReturnUrl || retainReturnUrl ? { returnUrl } : {};
    this.router
      .navigate([PAGE_URLS.LOGIN], { queryParams })
      .then(() => this.appService.setIsApplicationLoading(false));
  }

  /**
   * Removes the session data from local storage and resets the application state.
   */
  private removeSession() {
    this.appService.setIsLoggingOut(false);
    this.popupNotificationsService.clearNotifications();
    this.accountService.setUserAccessToken(null);
    this.storageService.removeFromLocalStorage(COOKIES.REFRESH_TOKEN);
    this.storageService.setLocalStorage(
      COOKIES.AUTH_EVENT,
      APP_AUTH_STATE.LOGGED_OUT,
    );
    this.authenticationService.setAuthenticatedAdmin(null);
  }
}
